import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Check, Search } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import {
  useSkills,
  useUpdateSkill,
  useDeleteSkill,
} from 'src/hooks/useSkillsData';
import SkillCard from './SkillCard';
import {
  skMissing,
  skTotal,
  SK_FIELD_FAIL,
  SK_STAR_TO_LEVEL,
  prefersReducedMotion,
} from './skillHelpers';
import { toApiPayload } from './skillsApi';
import './MySkills.css';

const UNDO_WINDOW_MS = 5000; // remove-undo grace period; delete is sent after this

const sortByExp = (list) => [...list].sort((a, b) => skTotal(b) - skTotal(a));

const REVEAL_DELAY = 540; // confirm exit animation duration (ms)

const cardNode = (id) => document.querySelector(`.skill-card[data-id="${id}"]`);

// Reading offsetWidth forces a synchronous reflow so a re-added animation class
// restarts its keyframes. Returned (not a bare expression) to satisfy no-void.
const reflow = (el) => el && el.offsetWidth;

export default function MySkills() {
  const { toast } = useToast();

  const updateSkillMutation = useUpdateSkill();
  const deleteSkillMutation = useDeleteSkill();

  // Skills load through TanStack Query; local state layers the optimistic UX
  // (inline edits, Gmail-style undo) on top and re-seeds whenever the query
  // data changes (initial load + post-write invalidation).
  const {
    data: skillsData,
    isLoading: loading,
    isError: loadError,
    refetch,
  } = useSkills();
  // Ordered most-experience-first for display.
  const [skills, setSkills] = useState([]);
  useEffect(() => {
    if (skillsData) setSkills(sortByExp(skillsData));
  }, [skillsData]);

  // Pending DELETE timers keyed by skill id — lets the Undo window cancel the
  // real delete before it's sent (Gmail-style).
  const pendingDeletes = useRef({});

  const [editing, setEditing] = useState({}); // id -> true (inline months editor open)
  const [expanded, setExpanded] = useState({}); // id -> true (confirmed card expanded)
  const [expandedPending, setExpandedPending] = useState({}); // id -> true (mobile pending row expanded)
  const [confirmingRemove, setConfirmingRemove] = useState({}); // id -> true (inline remove prompt showing)
  const [confirmingId, setConfirmingId] = useState(null); // id mid exit-animation
  const [introExpanded, setIntroExpanded] = useState(false); // mobile intro clamp
  const [liveMsg, setLiveMsg] = useState('');

  // Below the 768px shell breakpoint pending skills collapse to thin rows; the
  // layout must follow the viewport on resize/rotate, so track it in state.
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const patchSkill = useCallback((id, patch) => {
    setSkills((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );
  }, []);

  // ----- derived -----
  const visible = useMemo(
    () => skills.filter((s) => s.show && !s.is_suppressed),
    [skills]
  );
  const confirmedCount = visible.filter((s) => s.is_confirmed).length;
  const reviewCount = visible.length - confirmedCount;
  const pct = visible.length
    ? Math.round((confirmedCount / visible.length) * 100)
    : 0;
  const pending = visible.filter((s) => !s.is_confirmed);
  const confirmedList = visible.filter((s) => s.is_confirmed);
  const pendingCount = pending.length;

  // ----- imperative helpers (scroll / flash / focus / announce) -----
  const announce = useCallback((msg) => {
    setLiveMsg('');
    window.setTimeout(() => setLiveMsg(msg), 30);
  }, []);

  const focusCard = useCallback((id) => {
    const card = cardNode(id);
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.remove('skill-card--flash');
    reflow(card); // reflow so the animation re-triggers
    card.classList.add('skill-card--flash');
    window.setTimeout(() => card.classList.remove('skill-card--flash'), 1300);
    try {
      card.focus({ preventScroll: true });
    } catch {
      card.focus();
    }
  }, []);

  // ----- interactions -----
  const onEnterEdit = useCallback((id) => {
    setEditing((e) => ({ ...e, [id]: true }));
    // Opening the editor dismisses any pending remove prompt for this card.
    setConfirmingRemove((c) => {
      if (!c[id]) return c;
      const n = { ...c };
      delete n[id];
      return n;
    });
  }, []);
  const onCancelEdit = useCallback(
    (id) =>
      setEditing((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      }),
    []
  );

  // Saving records the number; it does NOT confirm (confirm stays deliberate).
  const onSaveEstimate = useCallback(
    (id, raw) => {
      const s = skills.find((x) => x.id === id);
      if (!s) return;
      const trimmed = String(raw).trim();
      let inputMonths;
      if (trimmed === '') {
        inputMonths = null;
      } else {
        let n = Math.round(Number(trimmed));
        if (Number.isNaN(n) || n < 0) {
          cardNode(id)?.querySelector('.skill-input')?.focus();
          return;
        }
        if (n > 600) {
          n = 600;
          toast({ message: 'Capped at 600 months — please double-check.' });
        }
        inputMonths = n;
      }
      // Local only — months persist on Confirm (pending) or Save changes
      // (confirmed). Editing a confirmed skill marks it dirty so the explicit
      // "Save" re-commits it via PUT.
      // Local only — no API call here, so no success toast. Persistence (and
      // its toast) happens on Confirm / Save changes.
      patchSkill(id, {
        inputMonths,
        ...(s.is_confirmed ? { dirtyEdit: true } : {}),
      });
      onCancelEdit(id);
    },
    [skills, patchSkill, onCancelEdit, toast]
  );

  // Set the 1–5 star rating and derive the proficiency level from it.
  const onSetStars = useCallback(
    (id, count) => {
      const s = skills.find((x) => x.id === id);
      patchSkill(id, {
        stars: count,
        proficiency: SK_STAR_TO_LEVEL[count] || null,
        ...(s?.is_confirmed ? { dirtyEdit: true } : {}),
      });
    },
    [skills, patchSkill]
  );

  const onSetRecency = useCallback(
    (id, band) => {
      const s = skills.find((x) => x.id === id);
      patchSkill(id, {
        recency: band,
        is_currently_used: band === 'CURRENT',
        ...(s?.is_confirmed ? { dirtyEdit: true } : {}),
      });
    },
    [skills, patchSkill]
  );

  const onSaveConfirmedEdits = useCallback(
    (id) => {
      const s = skills.find((x) => x.id === id);
      if (!s) return;
      patchSkill(id, { dirtyEdit: false }); // optimistic
      updateSkillMutation
        .mutateAsync({ id, body: toApiPayload(s) })
        .then(() => {
          const card = cardNode(id);
          if (card) {
            card.classList.remove('skill-card--success');
            reflow(card);
            card.classList.add('skill-card--success');
          }
          toast({ message: 'Changes saved' });
          // mutation invalidates ['skills'] on success — re-seeds local state
        })
        .catch(() => {
          // Revert so the "Save" button stays for a retry.
          patchSkill(id, { dirtyEdit: true });
          toast({
            message: 'Could not save — please retry.',
            variant: 'destructive',
          });
        });
    },
    [skills, patchSkill, toast, updateSkillMutation]
  );

  const onExpandConfirmed = useCallback((id) => {
    setExpanded((e) => ({ ...e, [id]: true }));
    window.setTimeout(() => {
      try {
        cardNode(id)?.focus({ preventScroll: true });
      } catch {
        /* no-op */
      }
    }, 0);
  }, []);
  const onCollapseConfirmed = useCallback(
    (id) =>
      setExpanded((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      }),
    []
  );

  // Mobile: open a collapsed pending row into its full editing card / fold back.
  const onExpandPending = useCallback((id) => {
    setExpandedPending((e) => ({ ...e, [id]: true }));
    window.setTimeout(() => {
      const card = cardNode(id);
      if (!card) return;
      try {
        card.focus({ preventScroll: true });
      } catch {
        /* no-op */
      }
      card.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }, []);
  const onCollapsePending = useCallback(
    (id) =>
      setExpandedPending((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      }),
    []
  );

  // Two-step removal: request shows the inline prompt; cancel backs out.
  const onRequestRemove = useCallback((id) => {
    setConfirmingRemove((c) => ({ ...c, [id]: true }));
    window.setTimeout(() => {
      cardNode(id)?.querySelector('.skill-btn--danger')?.focus?.();
    }, 0);
  }, []);
  const onCancelRemove = useCallback((id) => {
    setConfirmingRemove((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });
    window.setTimeout(() => {
      cardNode(id)?.querySelector('.skill-btn--remove')?.focus?.();
    }, 0);
  }, []);

  const onRestore = useCallback(
    (id) => {
      // Cancel the still-pending DELETE — nothing was sent to the server yet.
      const t = pendingDeletes.current[id];
      if (t) {
        clearTimeout(t);
        delete pendingDeletes.current[id];
      }
      const s = skills.find((x) => x.id === id);
      patchSkill(id, { is_suppressed: false });
      if (s) toast({ message: `Restored “${s.name}”` });
    },
    [skills, patchSkill, toast]
  );

  const onSuppress = useCallback(
    (id) => {
      const s = skills.find((x) => x.id === id);
      patchSkill(id, { is_suppressed: true, dirtyEdit: false });
      const drop = (id2) => (m) => {
        const n = { ...m };
        delete n[id2];
        return n;
      };
      setEditing(drop(id));
      setExpanded(drop(id));
      setExpandedPending(drop(id));
      setConfirmingRemove(drop(id));

      // Defer the real DELETE until the Undo window closes; Undo cancels it.
      if (pendingDeletes.current[id]) clearTimeout(pendingDeletes.current[id]);
      pendingDeletes.current[id] = window.setTimeout(() => {
        delete pendingDeletes.current[id];
        // mutation invalidates ['skills'] on success (re-seeds local state)
        deleteSkillMutation.mutateAsync(id).catch(() => {
          /* best-effort; the row is already hidden locally */
        });
      }, UNDO_WINDOW_MS);

      if (s) {
        toast({
          message: `Removed “${s.name}”`,
          duration: UNDO_WINDOW_MS,
          action: (
            <button
              type="button"
              className="skill-btn skill-btn--ghost"
              onClick={() => onRestore(id)}
            >
              Undo
            </button>
          ),
        });
      }
    },
    [skills, patchSkill, toast, onRestore, deleteSkillMutation]
  );

  // Confirm is the single completion gate — needs experience + proficiency + recency.
  const finalizeConfirm = useCallback(
    (id) => {
      const s = skills.find((x) => x.id === id);
      // Optimistically file the card into the confirmed stack.
      patchSkill(id, { is_confirmed: true, errorFlag: false });
      setEditing((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      });
      setConfirmingId(null);

      // Only declared a success once the PUT actually lands.
      const announceSuccess = () => {
        const live = skills.filter((x) => x.show && !x.is_suppressed);
        const done = live.filter((x) => x.is_confirmed || x.id === id).length;
        const remaining = live.length - done;
        const name = s?.name || 'Skill';

        if (remaining === 0) {
          toast({
            message: `All ${live.length} skills confirmed — you’re all set`,
          });
          announce(
            `Confirmed ${name}. All ${live.length} skills confirmed. You’re all set.`
          );
          return;
        }
        toast({ message: `Confirmed — ${done} of ${live.length} done` });

        const order = live.filter((x) => !x.is_confirmed && x.id !== id);
        const next = order[0];
        announce(
          `Confirmed ${name}. ${done} of ${live.length} confirmed.${
            next ? ` Next: ${next.name}.` : ''
          }`
        );
        if (next) window.setTimeout(() => focusCard(next.id), 160);
      };

      if (!s) {
        announceSuccess();
        return;
      }
      updateSkillMutation
        .mutateAsync({ id, body: toApiPayload(s) })
        .then(() => {
          announceSuccess();
          // mutation invalidates ['skills'] on success — re-seeds local state
        })
        .catch(() => {
          // Revert the optimistic confirm — the card returns to the queue.
          patchSkill(id, { is_confirmed: false });
          toast({
            message: 'Could not confirm — please retry.',
            variant: 'destructive',
          });
        });
    },
    [skills, patchSkill, toast, announce, focusCard, updateSkillMutation]
  );

  const onConfirm = useCallback(
    (id) => {
      const s = skills.find((x) => x.id === id);
      if (!s) return;
      const miss = skMissing(s);
      if (miss.length) {
        patchSkill(id, { errorFlag: true });
        if (miss.indexOf('experience') !== -1) {
          setEditing((e) => ({ ...e, [id]: true }));
        }
        window.setTimeout(() => {
          cardNode(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          let el;
          if (miss[0] === 'experience')
            el = document.getElementById(`skillInput_${id}`);
          else if (miss[0] === 'proficiency')
            el = document.querySelector(`#prof_${id} .skill-star`);
          else if (miss[0] === 'recency')
            el = document.querySelector(`#rec_${id} .skill-pill`);
          el?.focus?.();
        }, 80);
        toast({
          message:
            miss.length === 1
              ? SK_FIELD_FAIL[miss[0]]
              : 'Add the highlighted details to confirm',
        });
        return;
      }
      // Valid → subtle in-place exit, then file into the confirmed stack.
      if (prefersReducedMotion()) {
        finalizeConfirm(id);
      } else {
        setConfirmingId(id);
        window.setTimeout(() => finalizeConfirm(id), REVEAL_DELAY);
      }
    },
    [skills, patchSkill, toast, finalizeConfirm]
  );

  // Keep the confirming animation class on the right card across re-renders.
  useEffect(() => {
    if (confirmingId == null) return undefined;
    const card = cardNode(confirmingId);
    if (card) card.classList.add('skill-card--confirming');
    return () => {
      card?.classList.remove('skill-card--confirming');
    };
  }, [confirmingId]);

  const allDone = visible.length > 0 && reviewCount === 0;

  const renderCard = (s) => (
    <SkillCard
      key={s.id}
      skill={s}
      editing={!!editing[s.id]}
      expanded={!!expanded[s.id]}
      expandedPending={!!expandedPending[s.id]}
      isMobile={isMobile}
      confirmingRemove={!!confirmingRemove[s.id]}
      onConfirm={onConfirm}
      onEnterEdit={onEnterEdit}
      onCancelEdit={onCancelEdit}
      onSaveEstimate={onSaveEstimate}
      onSetStars={onSetStars}
      onSetRecency={onSetRecency}
      onSaveConfirmedEdits={onSaveConfirmedEdits}
      onSuppress={onSuppress}
      onRestore={onRestore}
      onExpandConfirmed={onExpandConfirmed}
      onCollapseConfirmed={onCollapseConfirmed}
      onExpandPending={onExpandPending}
      onCollapsePending={onCollapsePending}
      onRequestRemove={onRequestRemove}
      onCancelRemove={onCancelRemove}
    />
  );

  let body;
  if (loading) {
    body = (
      <div className="skills-empty">
        <p className="skills-empty-text">Loading your skills…</p>
      </div>
    );
  } else if (loadError) {
    body = (
      <div className="skills-empty">
        <p className="skills-empty-text">We couldn’t load your skills.</p>
        <button
          type="button"
          className="skill-btn skill-btn--restore skills-retry"
          onClick={() => refetch()}
        >
          Try again
        </button>
      </div>
    );
  } else if (visible.length === 0) {
    body = (
      <div className="skills-empty">
        <div className="skills-empty-icon">
          <Search aria-hidden="true" />
        </div>
        <p className="skills-empty-text">No skills detected yet.</p>
      </div>
    );
  } else if (pendingCount === 0) {
    // All confirmed → show the accordion (collapsed thin rows that expand),
    // not full plain cards.
    body = <ul className="skills-stack-list">{visible.map(renderCard)}</ul>;
  } else {
    body = (
      <>
        <ul className="skills-list">{pending.map(renderCard)}</ul>
        {confirmedList.length > 0 && (
          <div className="skills-confirmed-stack">
            <div className="skills-stack-head">
              <span className="skills-stack-title">Confirmed</span>
              <span className="skills-group-count">{confirmedList.length}</span>
              <span className="skills-group-line" />
            </div>
            <ul className="skills-stack-list">
              {confirmedList.map(renderCard)}
            </ul>
          </div>
        )}
      </>
    );
  }

  return (
    <section className="skills-view" aria-label="My skills">
      <div className="skills-head">
        <h1 className="skills-title">My Skills</h1>
        <p
          className={`skills-sub${introExpanded ? ' is-expanded' : ''}`}
          id="skillsSub"
        >
          We detected these skills from your application and work history.
          Review each one, then <strong>confirm</strong> what&apos;s right or{' '}
          <strong>correct</strong> anything that&apos;s off. Confirmed skills
          carry more weight when we match you to roles.
        </p>
        <button
          type="button"
          className="skills-sub-more"
          aria-expanded={introExpanded}
          aria-controls="skillsSub"
          onClick={() => setIntroExpanded((v) => !v)}
        >
          {introExpanded ? 'Less' : 'More'}
        </button>

        <div className="skills-summary" role="status" aria-live="polite">
          {allDone ? (
            <div className="skills-complete">
              <span className="skills-complete-icon">
                <Check strokeWidth={2.5} aria-hidden="true" />
              </span>
              <div>
                <div className="skills-complete-title">You&apos;re all set</div>
                <div className="skills-complete-sub">
                  All {visible.length} skills confirmed. We&apos;ll use these to
                  match you to roles.
                </div>
              </div>
            </div>
          ) : (
            <div className="skills-progress">
              <div
                className="skills-progress-track"
                role="progressbar"
                aria-valuenow={confirmedCount}
                aria-valuemin={0}
                aria-valuemax={visible.length}
                aria-label="Skills confirmed"
              >
                <span style={{ width: `${pct}%` }} />
              </div>
              <span className="skills-progress-text">
                <b>{confirmedCount}</b> of <b>{visible.length}</b> confirmed
              </span>
            </div>
          )}
        </div>

        <div
          aria-live="polite"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {liveMsg}
        </div>
      </div>

      {body}

      <footer className="skills-foot">
        <div className="skills-foot-links">
          <Link to="/terms">Terms &amp; Conditions</Link>
          <span className="dot">•</span>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
        <div className="skills-foot-copy">
          &copy; 2026 Ahedge. All rights reserved.
        </div>
      </footer>
    </section>
  );
}
